import React, { useState } from 'react';

const contactMethods = [
  {
    icon: 'ri-map-pin-line',
    title: 'Visit our training office',
    detail: 'No 6 Riboville Road, 183 Randjesfontein Country Estate, Midrand',
  },
  {
    icon: 'ri-phone-line',
    title: 'Speak to the team',
    detail: '+27 10 224 0406',
    href: 'tel:+27102240406',
  },
  {
    icon: 'ri-mail-line',
    title: 'Send an enquiry',
    detail: 'info@sylman.co.za',
    href: 'mailto:info@sylman.co.za',
  },
  {
    icon: 'ri-time-line',
    title: 'Office hours',
    detail: 'Monday to Friday, 08:30-16:30',
  },
];

const enquiryTypes = [
  'Corporate training',
  'Learnerships',
  'SETA compliance',
  'Programme information',
  'Partnership enquiry',
];

const initialForm = {
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  enquiryType: '',
  learners: '',
  message: '',
};

const ContactInfoSection: React.FC = () => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => formDataToSend.append(key, value));
      formDataToSend.append('formType', 'Contact Enquiry');

      const response = await fetch('/api/contact.php', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData(initialForm);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-wrap bg-[#f8f9fa]">
      <div className="section-inner">
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-start">
          <div className="reveal-left">
            <p className="section-label">Get in touch</p>
            <h2 className="section-heading">Start with the right conversation</h2>
            <p className="section-body mb-8">
              Tell us what you want to achieve and we will help you map the right training, compliance or workforce development path.
            </p>

            <div className="space-y-4">
              {contactMethods.map(item => {
                const content = (
                  <>
                    <div className="icon-box">
                      <i className={item.icon} />
                    </div>
                    <div>
                      <h3 className="font-sans text-sm font-semibold text-[#111111]">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-[#6b7280]">{item.detail}</p>
                    </div>
                  </>
                );

                return item.href ? (
                  <a key={item.title} href={item.href} className="card group flex gap-4 p-5 hover:no-underline">
                    {content}
                  </a>
                ) : (
                  <div key={item.title} className="card group flex gap-4 p-5">
                    {content}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card p-6 md:p-8 reveal-right shadow-green-soft">
            <div className="mb-7">
              <span className="eyebrow-card">Response within 24 hours</span>
              <h2 className="section-heading mt-4 mb-2">Request a consultation</h2>
              <p className="text-sm text-[#6b7280]">A few details are enough for us to recommend the next practical step.</p>
            </div>

            {submitStatus === 'success' ? (
              <div className="rounded-2xl bg-[#f0f9f2] border border-[#c8e6cf] p-8 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#2D7F3E]">
                  <i className="ri-check-line text-3xl" />
                </div>
                <h3 className="font-sans text-lg font-semibold text-[#111111]">Message received</h3>
                <p className="mt-2 text-sm text-[#6b7280]">Thank you. The Sylman team will contact you shortly.</p>
                <button className="btn btn-secondary btn-sm mt-6" onClick={() => setSubmitStatus('idle')}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="contact-form" className="space-y-5">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px]" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fullName" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Full name *</label>
                    <input id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required className="form-field" />
                  </div>
                  <div>
                    <label htmlFor="companyName" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Company</label>
                    <input id="companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} className="form-field" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="form-field" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Phone</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="form-field" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="enquiryType" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Enquiry type</label>
                    <select id="enquiryType" name="enquiryType" value={formData.enquiryType} onChange={handleInputChange} className="form-field">
                      <option value="">Select one</option>
                      {enquiryTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="learners" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">Estimated learners</label>
                    <input id="learners" name="learners" value={formData.learners} onChange={handleInputChange} placeholder="e.g. 15-30" className="form-field" />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wide text-[#3d3f42] mb-1.5">How can we help?</label>
                  <textarea id="message" name="message" rows={4} maxLength={500} value={formData.message} onChange={handleInputChange} className="form-field resize-none" />
                </div>

                <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-lg w-full">
                  {isSubmitting ? <><i className="ri-loader-4-line animate-spin mr-2" />Sending...</> : <><i className="ri-send-plane-line mr-2" />Send enquiry</>}
                </button>

                {submitStatus === 'error' && (
                  <p className="text-center text-sm text-red-600">Something went wrong. Please try again or call us directly.</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfoSection;
